import { apiUrl } from './Globals';
import { HTTP_GET } from './HttpService';

let cachedCategories = null;
let isFetching = false;
let fetchCallbacks = [];

export const getServiceCategoriesCache = async () => {
  if (cachedCategories) return cachedCategories;
  if (isFetching) {
    return new Promise((resolve) => {
      fetchCallbacks.push(resolve);
    });
  }
  isFetching = true;
  try {
    const res = await HTTP_GET(`${apiUrl}/servicecategory`);
    if (res && res.success && Array.isArray(res.data)) {
      cachedCategories = res.data;
    }
  } catch (err) {
    console.error('Failed to fetch service categories:', err);
  } finally {
    isFetching = false;
    fetchCallbacks.forEach((cb) => cb(cachedCategories));
    fetchCallbacks = [];
  }
  return cachedCategories || [];
};

/**
 * Calculates advance amount based on business rules:
 * 1. If configured profit percentage for a Service Category is 0%, advance amount MUST be ₹0.
 * 2. Otherwise: Advance Amount = Agreed Quotation Amount × Vendor Profit Percentage / 100
 */
export const calculateAdvanceAmount = (record, categoryName, categories = []) => {
  if (!record) return 0;

  const cats = (categories && categories.length > 0) ? categories : (cachedCategories || []);

  // 1. Identify matching Service Category by name/code
  let matchingCat = null;
  if (cats && cats.length > 0) {
    const lowerName = String(categoryName || '').trim().toLowerCase();
    matchingCat = cats.find((c) => {
      const cName = String(c.serviceCategoryName || '').trim().toLowerCase();
      const cCode = String(c.serviceCode || '').trim().toLowerCase();
      return cName === lowerName || cCode === lowerName || lowerName.includes(cName) || cName.includes(lowerName);
    });
  }

  // 2. Check if Service Category configured profit percentage is 0%
  if (matchingCat) {
    const catProfit = parseFloat(matchingCat.profitPercentage);
    if (matchingCat.profitPercentage === 0 || catProfit === 0) {
      return 0;
    }
  }

  if (record.serviceCategory && typeof record.serviceCategory === 'object') {
    const catProfit = parseFloat(record.serviceCategory.profitPercentage);
    if (record.serviceCategory.profitPercentage === 0 || catProfit === 0) {
      return 0;
    }
  }

  if (record.categoryProfitPercentage === 0 || record.serviceCategoryProfitPercentage === 0) {
    return 0;
  }

  // 3. Agreed Quotation Amount
  const amountAgreed = parseFloat(
    record.amountToBePaid ??
    record.amount ??
    record.quotationAmount ??
    record.agreedAmount ??
    record.totalAmount ??
    0
  );

  // 4. Vendor Profit Percentage (agreed by vendor during registration)
  let vendorProfitPct = null;

  if (record.vendorDetails) {
    if (Array.isArray(record.vendorDetails) && record.vendorDetails[0]) {
      const p = record.vendorDetails[0].profitPercentage ??
                record.vendorDetails[0].commissionPercentage ??
                record.vendorDetails[0].profit;
      if (p !== undefined && p !== null && p !== '' && parseFloat(p) > 0) {
        vendorProfitPct = parseFloat(p);
      }
    } else if (typeof record.vendorDetails === 'object') {
      const p = record.vendorDetails.profitPercentage ??
                record.vendorDetails.commissionPercentage ??
                record.vendorDetails.profit;
      if (p !== undefined && p !== null && p !== '' && parseFloat(p) > 0) {
        vendorProfitPct = parseFloat(p);
      }
    }
  }

  if (vendorProfitPct === null || isNaN(vendorProfitPct) || vendorProfitPct <= 0) {
    const p = record.commissionPercentage ??
              record.profitPercentage ??
              record.vendorProfitPercentage;
    if (p !== undefined && p !== null && p !== '' && parseFloat(p) > 0) {
      vendorProfitPct = parseFloat(p);
    }
  }

  // Calculate using formula: Agreed Amount * Profit Percentage / 100
  if (amountAgreed > 0 && vendorProfitPct !== null && !isNaN(vendorProfitPct) && vendorProfitPct > 0) {
    return Math.round((amountAgreed * vendorProfitPct) / 100);
  }

  // Fallback to record.advanceAmount if present and > 0
  if (record.advanceAmount !== undefined && record.advanceAmount !== null && record.advanceAmount !== '' && parseFloat(record.advanceAmount) > 0) {
    return Math.round(parseFloat(record.advanceAmount));
  }

  // Fallback to record.amountRequested if present and > 0
  if (record.amountRequested !== undefined && record.amountRequested !== null && record.amountRequested !== '' && parseFloat(record.amountRequested) > 0) {
    return Math.round(parseFloat(record.amountRequested));
  }

  // Fallback to Category configured profit percentage if vendor profit pct is not set or 0
  if (matchingCat && amountAgreed > 0) {
    const catProfit = parseFloat(matchingCat.profitPercentage);
    if (!isNaN(catProfit) && catProfit > 0) {
      return Math.round((amountAgreed * catProfit) / 100);
    }
  }

  return 0;
};
